<template>
  <div class="guide-webhooks">
    <h2>What are webhooks?</h2>
    <p>
      Webhooks are HTTP POST callbacks that deliver real-time event
      notifications to your server when things happen in the signing lifecycle.
      Instead of polling the API repeatedly to check whether a signer has
      completed or a package has expired, webhooks push updates to you the
      instant they occur.
    </p>
    <p>
      Register a URL on your server, tell the platform which events you care
      about, and an HTTP POST is sent to that URL every time one of those
      events fires. Your server processes the payload and responds with a 2xx
      status code to acknowledge receipt.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Why webhooks matter</h2>
    <ul>
      <li>
        <strong>Real-time.</strong> Know the instant a signer completes their
        fields, rather than discovering it on your next polling cycle 30
        seconds or 5 minutes later.
      </li>
      <li>
        <strong>Efficient.</strong> No wasted API calls checking for unchanged
        status. Each webhook delivery carries actionable information. Something
        actually happened.
      </li>
      <li>
        <strong>Reliable.</strong> Automatic retry with exponential backoff
        ensures delivery even when your server is temporarily unavailable.
      </li>
      <li>
        <strong>Handles concurrent volume.</strong> Supports thousands of
        concurrent packages without the polling overhead that would otherwise
        grow linearly with your volume.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Event types</h2>
    <p>
      Webhook events cover every state transition in the signing lifecycle. There are 30
      event types grouped into five categories. Subscribe by listing the exact event names
      in the <code>events</code> array at creation, or use the wildcard <code>"*"</code>
      to subscribe to everything.
    </p>

    <h3>Package lifecycle</h3>
    <ul>
      <li><code>package.created</code>. Draft package created (API or portal).</li>
      <li><code>package.sent</code>. Package sent to signers. Notifications dispatched.</li>
      <li><code>package.completed</code>. All signers finished. Final documents ready.</li>
      <li><code>package.declined</code>. A signer declined. The package is terminal.</li>
      <li><code>package.voided</code>. Sender voided the package.</li>
      <li><code>package.expired</code>. Expiration date passed without completion.</li>
      <li><code>package.send_failed</code>. Send failed during document processing or notification dispatch.</li>
      <li><code>package.approval_denied</code>. An Approver-type signer denied approval.</li>
    </ul>

    <h3>Signer actions</h3>
    <ul>
      <li><code>signer.notified</code>. Notification email or SMS delivered to the signer.</li>
      <li><code>signer.viewed</code>. Signer opened the signing ceremony.</li>
      <li><code>signer.completed</code>. Signer finished all assigned fields.</li>
      <li><code>signer.declined</code>. Signer declined to sign.</li>
      <li><code>signer.authenticated</code>. Signer passed a secondary auth step (AccessCode, SMS, KBA, or IDV).</li>
      <li><code>signer.auth_failed</code>. Signer failed a secondary auth step.</li>
      <li><code>signer.reassigned</code>. The sender reassigned the signer slot to a new person.</li>
      <li><code>signer.kba_exhausted</code>. Signer used all KBA attempts without passing.</li>
      <li><code>signer.idv_exhausted</code>. Signer used all IDV attempts without passing.</li>
    </ul>

    <h3>Payments</h3>
    <ul>
      <li><code>payment.succeeded</code>, <code>payment.failed</code>, <code>payment.authorized</code></li>
      <li><code>payment.method_saved</code>, <code>payment.refunded</code></li>
      <li><code>payment.recovery_initiated</code>, <code>payment.batch_completed</code></li>
      <li><code>payment.dispute.created</code>, <code>payment.dispute.closed</code></li>
    </ul>

    <h3>Documents and annotations</h3>
    <ul>
      <li><code>document.uploaded</code>. A document was attached to a package.</li>
      <li><code>document.processed</code>. Async processing (PDF conversion, page-image generation, tag detection) completed successfully.</li>
      <li><code>document.processing_failed</code>. Async processing failed. Inspect the document's <code>processingError</code>.</li>
      <li><code>annotation.completed</code>. A signer completed a single field.</li>
    </ul>

    <p>
      The exact list of supported event types is published in the portal on
      the Webhooks page, and in the <a href="/Docs/ApiReference">API Reference</a>
      under the event-types discovery endpoint.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Configuring webhooks</h2>
    <p>
      Webhook subscriptions can be configured either through the portal
      (<strong>Settings</strong> then <strong>Webhooks</strong>) or
      programmatically. Both paths reach the same backend. Creating
      subscriptions, retrieving signing secrets, inspecting delivery history,
      retrying dead-lettered deliveries, and firing test events are all
      available via API to a credential carrying the
      <code>sign.webhook.manage</code> scope.
    </p>

    <q-banner class="bg-blue-1 q-mb-md" rounded>
      <template v-slot:avatar>
        <q-icon name="info" color="blue-9" />
      </template>
      <strong>Mint a credential with <code>sign.webhook.manage</code> only when
      the integration genuinely needs it.</strong> Granting it broadly lets any
      caller redirect your event stream to a different endpoint, so treat it
      with the same care as any tenant-admin operation. For most integrations,
      configuring webhooks once via the portal and then leaving them alone is
      the simpler posture.
    </q-banner>

    <p>
      On the Webhooks page, click <strong>New Webhook</strong> to register an
      endpoint URL, pick the events you want to subscribe to (or check "All
      events"), and save. The full signing secret is shown once at creation.
      Copy it into your server's secret store immediately. Each subscription
      on the page shows its recent delivery history (Succeeded, Failed,
      Retrying, DeadLettered) with response codes and durations. Click
      <strong>Test</strong> to fire a synthetic test event to your endpoint
      without waiting for real activity. Dead-lettered deliveries can be
      manually retried from the same page. Secrets can be rotated from the
      subscription detail view.
    </p>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      This is the section you care about if you are writing the webhook
      handler on your server. Configuration happens in the portal, but
      receiving and verifying deliveries is where the code lives. Below is
      the delivery format, the HMAC verification pattern (with working
      reference implementations in seven languages), and the retry lifecycle
      you should design your handler to handle.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Delivery format</h3>
    <p>
      Every delivery is an HTTP POST with three identifying headers and a
      flat JSON body. The headers carry the event type, a unique delivery
      ID, and the HMAC signature. The body contains the event payload.
    </p>
    <GuideCodeTabs :samples="samples.deliveryExample" />

    <h3>Headers</h3>
    <ul>
      <li><code>X-ESign-Event</code>. Event type, for example <code>package.completed</code>. Use this to dispatch in your handler without parsing the body.</li>
      <li><code>X-ESign-Delivery</code>. Unique delivery GUID. Use for idempotency by recording IDs you have processed and skipping repeats.</li>
      <li><code>X-ESign-Signature</code>. HMAC signature in the form <code>t=&lt;unixTimestamp&gt;,v1=&lt;hexHmacSha256&gt;</code>.</li>
    </ul>

    <h3>Body</h3>
    <p>
      Payloads are flat. There is no <code>data</code> wrapper and no
      generic <code>eventId</code> envelope. Each event type defines its own
      top-level fields. Common fields across most events:
    </p>
    <ul>
      <li><code>tenantId</code>. Your tenant's numeric ID.</li>
      <li><code>packageGuid</code>. The package GUID the event relates to.</li>
      <li><code>timestamp</code>. ISO-8601 UTC timestamp when the event fired.</li>
    </ul>
    <p>
      Signer events add <code>signerGuid</code>, <code>signerEmail</code>,
      and <code>signerName</code>. Payment events add
      <code>paymentGuid</code> and relevant amounts. See the event-types
      endpoint for the exact schema per event.
    </p>

    <q-separator class="q-my-lg" />

    <h3>HMAC signature verification</h3>
    <p>
      Every delivery carries an <code>X-ESign-Signature</code> header
      containing a timestamp and an HMAC-SHA256 signature. Verify both before
      you trust the payload.
    </p>

    <q-banner class="bg-red-1 text-red-9 q-mb-md" rounded>
      <template v-slot:avatar>
        <q-icon name="security" color="red-9" />
      </template>
      <strong>Security:</strong> Always verify the signature before
      processing. The timestamp is part of the signed input, so a replay
      attacker cannot swap it out. Reject any delivery whose timestamp is
      more than a few minutes old to defend against replay.
    </q-banner>

    <h3>Header format</h3>
    <GuideCodeTabs :samples="samples.signatureHeader" />
    <ul>
      <li><code>t</code>. Unix timestamp (in seconds) when the delivery was signed.</li>
      <li><code>v1</code>. Lowercase hex HMAC-SHA256 of the signed input.</li>
    </ul>

    <h3>Signed input</h3>
    <p>
      The signed input is <code>"{timestamp}.{rawJsonBody}"</code>, which
      means the timestamp, a literal period, and the raw request body as
      received. Do not re-serialize the body before computing the signature.
      JSON key order and whitespace must match byte-for-byte.
    </p>
    <GuideCodeTabs :samples="samples.signedInputPseudo" />

    <h3>Working verification handlers</h3>
    <p>
      Reference handler implementations are available in every language the
      platform supports. Each one reads the raw request body, validates the
      timestamp window, checks the HMAC in constant time, and returns 401 on
      any failure. Adapt the routing to your framework of choice.
    </p>
    <GuideCodeTabs :samples="samples.verifyHandler" />

    <q-separator class="q-my-lg" />

    <h3>Delivery statuses and retries</h3>
    <p>
      Every delivery moves through a lifecycle the portal tracks on the
      subscription detail view. The table below lists the status values you
      will see there.
    </p>
    <q-markup-table flat bordered dense class="q-mb-md">
      <thead>
        <tr><th class="text-left">Status</th><th class="text-left">Meaning</th></tr>
      </thead>
      <tbody>
        <tr><td><code>Pending</code></td><td>Queued, not yet attempted.</td></tr>
        <tr><td><code>Processing</code></td><td>Actively being delivered.</td></tr>
        <tr><td><code>Succeeded</code></td><td>Your endpoint returned a 2xx status.</td></tr>
        <tr><td><code>Failed</code></td><td>Most recent attempt failed. More retries are still scheduled.</td></tr>
        <tr><td><code>Retrying</code></td><td>Waiting in the retry queue.</td></tr>
        <tr><td><code>DeadLettered</code></td><td>All automatic retries exhausted. Eligible for manual retry from the portal.</td></tr>
      </tbody>
    </q-markup-table>
    <p>
      Failures (non-2xx responses or timeouts) are retried automatically with
      exponential backoff over several hours. If retries are exhausted the
      delivery moves to <code>DeadLettered</code>, where it stays until a
      portal administrator manually retries or discards it. Dead-lettered
      deliveries preserve the exact original payload, so a manual retry
      sends the same signature and body a fresh receiver can verify.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Testing your receiver</h3>
    <p>
      During development, open <strong>Settings</strong> then
      <strong>Webhooks</strong> in the portal, pick your subscription, and
      click <strong>Test</strong>. That fires a synthetic event with
      realistic sample data to your endpoint. The request carries the same
      headers and HMAC signature as a real delivery, so you can verify that
      your endpoint is reachable, parses the payload, checks the signature,
      and returns a 2xx response before any real signing activity runs
      through it.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Secret rotation</h3>
    <p>
      If your webhook secret is compromised or you need to rotate it as part
      of your security policy, open the subscription detail view in the
      portal and click <strong>Regenerate Secret</strong>. The new secret is
      shown once. The old secret is invalidated immediately on the sending
      side. Deliveries already in flight carry signatures computed with the
      old secret and will continue to arrive for a short period. To avoid
      verification failures during the rotation window, configure your
      endpoint to accept either secret for a few minutes. Once you confirm
      no more old-secret deliveries are arriving, remove the old secret
      from your verification list.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Best practices</h3>
    <ul>
      <li>
        <strong>Always verify HMAC signatures.</strong> Never process a
        payload without verifying the <code>X-ESign-Signature</code> header.
        The signed input includes the timestamp, so verification also
        protects against stale replay.
      </li>
      <li>
        <strong>Return 2xx quickly (within 5 seconds).</strong> Acknowledge
        receipt immediately and do any heavy processing asynchronously. If
        your handler takes too long, the delivery times out and triggers
        unnecessary retries.
      </li>
      <li>
        <strong>Process events idempotently.</strong> The same event may
        arrive more than once, for example if your 2xx response is lost in
        transit or if you manually retry a dead-lettered delivery. Record
        the <code>X-ESign-Delivery</code> GUID and skip repeats.
      </li>
      <li>
        <strong>Subscribe only to events you need.</strong> This reduces
        noise, processing load, and the surface area for errors. You can
        always add more event subscriptions later.
      </li>
      <li>
        <strong>Monitor delivery history in the portal for persistent
        failures.</strong> Repeated failures may indicate a misconfigured
        endpoint, a firewall blocking our requests, or an application bug.
        The Webhooks page in the portal shows response codes and timing on
        every delivery attempt. Review it regularly to catch issues early.
      </li>
      <li>
        <strong>Use the portal's Test button during development.</strong>
        Verify your handler end-to-end before going live. That catches
        serialization issues, signature-verification bugs, and routing
        problems before they affect real signing workflows.
      </li>
    </ul>

    <q-banner class="bg-blue-1 text-blue-9 q-mt-lg" rounded>
      <strong>Tip:</strong> Combine webhooks with embedded signing for the
      most responsive integration. Use the <code>signer.completed</code>
      webhook to trigger downstream workflows (such as provisioning access
      or sending confirmation emails) the instant each signer finishes,
      rather than waiting for the redirect callback.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const API = 'https://api.esignlaunchpad.com';

const samples = {
    // ─── GET /webhooks/event-types (no scope required, just auth) ──────────────
    listEventTypes: {
        curl: `curl ${API}/api/v1/webhooks/event-types \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch('${API}/api/v1/webhooks/event-types', {
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN }
});
const eventTypes = await response.json();`,

        node: `const response = await fetch('${API}/api/v1/webhooks/event-types', {
  headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN }
});
const eventTypes = await response.json();`,

        python: `response = requests.get(
    '${API}/api/v1/webhooks/event-types',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
event_types = response.json()`,

        csharp: `var eventTypes = await client.GetFromJsonAsync<JsonElement>(
    "api/v1/webhooks/event-types");`,

        php: `$response = $client->get('api/v1/webhooks/event-types', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$eventTypes = json_decode($response->getBody(), true);`,

        ruby: `uri = URI('${API}/api/v1/webhooks/event-types')
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
event_types = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)`
    },

    // ─── POST /webhooks (create subscription) ───────────────────────────────────
    createSubscription: {
        curl: `curl -X POST ${API}/api/v1/webhooks \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "url": "https://yourapp.com/webhooks/esign",
    "events": ["package.completed", "signer.completed", "package.voided"],
    "description": "Production signing events"
  }'`,

        js: `const response = await fetch('${API}/api/v1/webhooks', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    url: 'https://yourapp.com/webhooks/esign',
    events: ['package.completed', 'signer.completed', 'package.voided'],
    description: 'Production signing events'
  })
});
const subscription = await response.json();`,

        node: `const response = await fetch('${API}/api/v1/webhooks', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    url: 'https://yourapp.com/webhooks/esign',
    events: ['package.completed', 'signer.completed', 'package.voided'],
    description: 'Production signing events'
  })
});
const subscription = await response.json();
console.log('Subscription id:', subscription.id);`,

        python: `response = requests.post(
    '${API}/api/v1/webhooks',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'url': 'https://yourapp.com/webhooks/esign',
        'events': ['package.completed', 'signer.completed', 'package.voided'],
        'description': 'Production signing events'
    }
)
response.raise_for_status()
subscription = response.json()`,

        csharp: `var payload = new
{
    url = "https://yourapp.com/webhooks/esign",
    events = new[] { "package.completed", "signer.completed", "package.voided" },
    description = "Production signing events"
};
var response = await client.PostAsJsonAsync("api/v1/webhooks", payload);
response.EnsureSuccessStatusCode();
var subscription = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post('api/v1/webhooks', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'url' => 'https://yourapp.com/webhooks/esign',
        'events' => ['package.completed', 'signer.completed', 'package.voided'],
        'description' => 'Production signing events',
    ],
]);
$subscription = json_decode($response->getBody(), true);`,

        ruby: `uri = URI('${API}/api/v1/webhooks')
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  url: 'https://yourapp.com/webhooks/esign',
  events: ['package.completed', 'signer.completed', 'package.voided'],
  description: 'Production signing events'
}.to_json
subscription = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)`
    },

    // ─── GET /webhooks/{id}/secret (reveal full signing secret) ─────────────────
    revealSecret: {
        curl: `curl ${API}/api/v1/webhooks/$WEBHOOK_ID/secret \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`${API}/api/v1/webhooks/\${webhookId}/secret\`,
  { headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const { secret } = await response.json();`,

        node: `const response = await fetch(
  \`${API}/api/v1/webhooks/\${webhookId}/secret\`,
  { headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const { secret } = await response.json();
// Store this in your secret manager immediately - treat it like a password.`,

        python: `response = requests.get(
    f'${API}/api/v1/webhooks/{webhook_id}/secret',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()
secret = response.json()['secret']`,

        csharp: `var body = await client.GetFromJsonAsync<JsonElement>(
    $"api/v1/webhooks/{webhookId}/secret");
var secret = body.GetProperty("secret").GetString();`,

        php: `$response = $client->get("api/v1/webhooks/{$webhookId}/secret", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$secret = json_decode($response->getBody(), true)['secret'];`,

        ruby: `uri = URI("${API}/api/v1/webhooks/#{webhook_id}/secret")
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
secret = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)['secret']`
    },

    // ─── Sample delivery (HTTP wire format, single tab) ─────────────────────────
    deliveryExample: {
        http: `POST /webhooks/esign HTTP/1.1
Host: yourapp.com
Content-Type: application/json
X-ESign-Event: package.completed
X-ESign-Delivery: 7c1d4a2e-5b9f-4c3d-8f2e-1a6b7c9d0e4f
X-ESign-Signature: t=1741879800,v1=a1b2c3d4e5f6...

{
  "tenantId": 42,
  "packageGuid": "550e8400-e29b-41d4-a716-446655440000",
  "packageName": "Q1 Sales Agreement",
  "completedOn": "2026-03-01T14:29:58Z",
  "totalSigners": 2,
  "totalAnnotations": 6,
  "timestamp": "2026-03-01T14:30:00Z"
}`
    },

    // ─── Signature header format + pseudocode (single text tab each) ────────────
    signatureHeader: {
        text: `X-ESign-Signature: t=1741879800,v1=a1b2c3d4e5f6...`
    },

    signedInputPseudo: {
        text: `signedInput = t + "." + rawBodyBytes
expected    = HMAC-SHA256(webhookSecret, signedInput)  // hex lowercase
valid       = constantTimeEquals(expected, v1Value)`
    },

    // ─── Working verification handlers in every language ────────────────────────
    verifyHandler: {
        js: `// Browser / edge-function style (Web Crypto). For Node, see the Node.js tab.
async function verifyEsignWebhook(request, secret) {
  const header = request.headers.get('X-ESign-Signature') || '';
  const parts = Object.fromEntries(
    header.split(',').map(p => [p.split('=')[0], p.split('=').slice(1).join('=')])
  );
  const timestamp = parts.t;
  const v1 = parts.v1;

  if (!timestamp || Math.abs(Date.now() / 1000 - Number(timestamp)) > 300) {
    return false; // reject stale (5-minute window)
  }

  const rawBody = await request.arrayBuffer();
  const signedInput = new Uint8Array(
    new TextEncoder().encode(timestamp + '.').length + rawBody.byteLength
  );
  signedInput.set(new TextEncoder().encode(timestamp + '.'), 0);
  signedInput.set(new Uint8Array(rawBody), new TextEncoder().encode(timestamp + '.').length);

  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false, ['sign']
  );
  const sig = await crypto.subtle.sign('HMAC', key, signedInput);
  const hex = Array.from(new Uint8Array(sig))
    .map(b => b.toString(16).padStart(2, '0')).join('');

  return constantTimeEquals(hex, v1);
}

function constantTimeEquals(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}`,

        node: `const crypto = require('node:crypto');
const express = require('express');

const app = express();

// Capture the RAW body as a Buffer so we can sign it byte-for-byte.
app.post('/webhooks/esign',
  express.raw({ type: 'application/json' }),
  (req, res) => {
    const header = req.header('X-ESign-Signature') || '';
    const parts = Object.fromEntries(
      header.split(',').map(p => {
        const i = p.indexOf('=');
        return [p.slice(0, i), p.slice(i + 1)];
      })
    );
    const timestamp = parts.t;
    const v1 = parts.v1;

    if (!timestamp || Math.abs(Date.now() / 1000 - Number(timestamp)) > 300) {
      return res.status(401).send('stale');
    }

    const signedInput = Buffer.concat([
      Buffer.from(timestamp + '.'),
      req.body
    ]);
    const expected = crypto
      .createHmac('sha256', process.env.ESIGN_WEBHOOK_SECRET)
      .update(signedInput)
      .digest('hex');

    if (!v1 || !crypto.timingSafeEqual(
            Buffer.from(expected, 'hex'),
            Buffer.from(v1, 'hex'))) {
      return res.status(401).send('bad signature');
    }

    const event = JSON.parse(req.body.toString('utf8'));
    const eventType = req.header('X-ESign-Event');
    const deliveryId = req.header('X-ESign-Delivery');

    // Acknowledge quickly, process asynchronously.
    console.log(\`Received \${eventType} delivery \${deliveryId}\`);
    res.status(200).send('ok');
  }
);

app.listen(3000);`,

        python: `import hmac, hashlib, os, time
from flask import Flask, request, abort

app = Flask(__name__)
SECRET = os.environ['ESIGN_WEBHOOK_SECRET'].encode()

@app.post('/webhooks/esign')
def esign_webhook():
    header = request.headers.get('X-ESign-Signature', '')
    parts = dict(p.split('=', 1) for p in header.split(',') if '=' in p)
    timestamp = parts.get('t', '')
    v1 = parts.get('v1', '')

    if not timestamp or abs(int(time.time()) - int(timestamp)) > 300:
        abort(401)

    raw_body = request.get_data()  # bytes, before any parsing
    signed_input = timestamp.encode() + b'.' + raw_body
    expected = hmac.new(SECRET, signed_input, hashlib.sha256).hexdigest()

    if not hmac.compare_digest(expected, v1):
        abort(401)

    event_type = request.headers.get('X-ESign-Event')
    delivery_id = request.headers.get('X-ESign-Delivery')
    # dispatch on event_type, use delivery_id for idempotency...
    return '', 200`,

        csharp: `// ASP.NET Core minimal-API handler. The raw request body must be read
// before any model binding touches it.
using System.Security.Cryptography;
using System.Text;

app.MapPost("/webhooks/esign", async (HttpContext ctx, IConfiguration config) =>
{
    var header = ctx.Request.Headers["X-ESign-Signature"].ToString();
    var parts = header.Split(',')
        .Select(p => p.Split('=', 2))
        .Where(x => x.Length == 2)
        .ToDictionary(x => x[0], x => x[1]);

    if (!parts.TryGetValue("t", out var ts) ||
        !parts.TryGetValue("v1", out var v1) ||
        !long.TryParse(ts, out var tsVal))
    {
        return Results.Unauthorized();
    }

    var ageSeconds = Math.Abs(DateTimeOffset.UtcNow.ToUnixTimeSeconds() - tsVal);
    if (ageSeconds > 300) return Results.Unauthorized();

    // Read the RAW body byte-for-byte.
    using var ms = new MemoryStream();
    await ctx.Request.Body.CopyToAsync(ms);
    var rawBody = ms.ToArray();

    var signedInput = new byte[Encoding.UTF8.GetByteCount(ts + ".") + rawBody.Length];
    var prefixLen = Encoding.UTF8.GetBytes(ts + ".", signedInput);
    Buffer.BlockCopy(rawBody, 0, signedInput, prefixLen, rawBody.Length);

    var secret = Encoding.UTF8.GetBytes(config["ESIGN_WEBHOOK_SECRET"]!);
    using var hmac = new HMACSHA256(secret);
    var expectedBytes = hmac.ComputeHash(signedInput);
    var expectedHex = Convert.ToHexString(expectedBytes).ToLowerInvariant();

    // Constant-time comparison
    var v1Bytes = Convert.FromHexString(v1);
    if (!CryptographicOperations.FixedTimeEquals(expectedBytes, v1Bytes))
    {
        return Results.Unauthorized();
    }

    var eventType = ctx.Request.Headers["X-ESign-Event"].ToString();
    var deliveryId = ctx.Request.Headers["X-ESign-Delivery"].ToString();
    // dispatch on eventType, use deliveryId for idempotency...
    return Results.Ok();
});`,

        php: `<?php
// PSR-7 style handler or plain Apache / nginx entry point. Read php://input
// BEFORE any framework middleware parses the body.

function verify_esign_webhook(string $header, string $rawBody, string $secret): bool {
    $parts = [];
    foreach (explode(',', $header) as $part) {
        if (($eq = strpos($part, '=')) !== false) {
            $parts[substr($part, 0, $eq)] = substr($part, $eq + 1);
        }
    }
    $timestamp = $parts['t'] ?? null;
    $v1        = $parts['v1'] ?? null;

    if (!$timestamp || abs(time() - (int)$timestamp) > 300) {
        return false;
    }

    $signedInput = $timestamp . '.' . $rawBody;
    $expected = hash_hmac('sha256', $signedInput, $secret);

    return hash_equals($expected, $v1 ?? '');
}

$header = $_SERVER['HTTP_X_ESIGN_SIGNATURE'] ?? '';
$rawBody = file_get_contents('php://input');
$secret = getenv('ESIGN_WEBHOOK_SECRET');

if (!verify_esign_webhook($header, $rawBody, $secret)) {
    http_response_code(401);
    exit;
}

$event = json_decode($rawBody, true);
$eventType  = $_SERVER['HTTP_X_ESIGN_EVENT'] ?? '';
$deliveryId = $_SERVER['HTTP_X_ESIGN_DELIVERY'] ?? '';
// dispatch on $eventType, use $deliveryId for idempotency...
http_response_code(200);`,

        ruby: `# Sinatra handler. Read raw body BEFORE anything else.
require 'sinatra'
require 'openssl'

SECRET = ENV.fetch('ESIGN_WEBHOOK_SECRET')

post '/webhooks/esign' do
  header = request.env['HTTP_X_ESIGN_SIGNATURE'] || ''
  parts = header.split(',').map { |p| p.split('=', 2) }.to_h
  timestamp = parts['t']
  v1 = parts['v1']

  halt 401, 'stale' if timestamp.nil? || (Time.now.to_i - timestamp.to_i).abs > 300

  request.body.rewind
  raw_body = request.body.read
  signed_input = "#{timestamp}.#{raw_body}"
  expected = OpenSSL::HMAC.hexdigest('SHA256', SECRET, signed_input)

  halt 401, 'bad signature' unless Rack::Utils.secure_compare(expected, v1 || '')

  event_type  = request.env['HTTP_X_ESIGN_EVENT']
  delivery_id = request.env['HTTP_X_ESIGN_DELIVERY']
  # dispatch on event_type, use delivery_id for idempotency...
  status 200
  body 'ok'
end`
    },

    // ─── GET /webhooks/{id}/deliveries ─────────────────────────────────────────
    listDeliveries: {
        curl: `curl "${API}/api/v1/webhooks/$WEBHOOK_ID/deliveries?limit=50&status=DeadLettered" \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const url = new URL(\`${API}/api/v1/webhooks/\${webhookId}/deliveries\`);
url.searchParams.set('limit', '50');
url.searchParams.set('status', 'DeadLettered');

const response = await fetch(url, { headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } });
const deliveries = await response.json();`,

        node: `const url = new URL(\`${API}/api/v1/webhooks/\${webhookId}/deliveries\`);
url.searchParams.set('limit', '50');
url.searchParams.set('status', 'DeadLettered');

const response = await fetch(url, { headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } });
const deliveries = await response.json();`,

        python: `response = requests.get(
    f'${API}/api/v1/webhooks/{webhook_id}/deliveries',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    params={'limit': 50, 'status': 'DeadLettered'}
)
deliveries = response.json()`,

        csharp: `var deliveries = await client.GetFromJsonAsync<JsonElement>(
    $"api/v1/webhooks/{webhookId}/deliveries?limit=50&status=DeadLettered");`,

        php: `$response = $client->get("api/v1/webhooks/{$webhookId}/deliveries", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'query' => ['limit' => 50, 'status' => 'DeadLettered'],
]);
$deliveries = json_decode($response->getBody(), true);`,

        ruby: `uri = URI("${API}/api/v1/webhooks/#{webhook_id}/deliveries")
uri.query = URI.encode_www_form(limit: 50, status: 'DeadLettered')
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
deliveries = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)`
    },

    // ─── POST retry a dead-lettered delivery ───────────────────────────────────
    retryDelivery: {
        curl: `curl -X POST ${API}/api/v1/webhooks/$WEBHOOK_ID/deliveries/$DELIVERY_ID/retry \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `await fetch(
  \`${API}/api/v1/webhooks/\${webhookId}/deliveries/\${deliveryId}/retry\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);`,

        node: `const response = await fetch(
  \`${API}/api/v1/webhooks/\${webhookId}/deliveries/\${deliveryId}/retry\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
if (response.status === 409) console.warn('Delivery is not dead-lettered - nothing to retry');`,

        python: `response = requests.post(
    f'${API}/api/v1/webhooks/{webhook_id}/deliveries/{delivery_id}/retry',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
if response.status_code == 409:
    print('Delivery is not dead-lettered - nothing to retry')
else:
    response.raise_for_status()`,

        csharp: `var response = await client.PostAsync(
    $"api/v1/webhooks/{webhookId}/deliveries/{deliveryId}/retry", null);
if ((int)response.StatusCode == 409)
    Console.WriteLine("Delivery is not dead-lettered - nothing to retry");
else
    response.EnsureSuccessStatusCode();`,

        php: `try {
    $client->post("api/v1/webhooks/{$webhookId}/deliveries/{$deliveryId}/retry", [
        'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    ]);
} catch (\\GuzzleHttp\\Exception\\ClientException $e) {
    if ($e->getResponse()->getStatusCode() === 409) {
        echo "Delivery is not dead-lettered - nothing to retry\\n";
    } else { throw $e; }
}`,

        ruby: `uri = URI("${API}/api/v1/webhooks/#{webhook_id}/deliveries/#{delivery_id}/retry")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
puts 'Delivery is not dead-lettered - nothing to retry' if res.code == '409'`
    },

    // ─── GET a single delivery (with full payloadJson) ──────────────────────────
    getDelivery: {
        curl: `curl ${API}/api/v1/webhooks/$WEBHOOK_ID/deliveries/$DELIVERY_ID \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`${API}/api/v1/webhooks/\${webhookId}/deliveries/\${deliveryId}\`,
  { headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const delivery = await response.json();
console.log('Original payload:', delivery.payloadJson);`,

        node: `const response = await fetch(
  \`${API}/api/v1/webhooks/\${webhookId}/deliveries/\${deliveryId}\`,
  { headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const delivery = await response.json();`,

        python: `response = requests.get(
    f'${API}/api/v1/webhooks/{webhook_id}/deliveries/{delivery_id}',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
delivery = response.json()`,

        csharp: `var delivery = await client.GetFromJsonAsync<JsonElement>(
    $"api/v1/webhooks/{webhookId}/deliveries/{deliveryId}");`,

        php: `$response = $client->get(
    "api/v1/webhooks/{$webhookId}/deliveries/{$deliveryId}",
    ['headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')]]
);
$delivery = json_decode($response->getBody(), true);`,

        ruby: `uri = URI("${API}/api/v1/webhooks/#{webhook_id}/deliveries/#{delivery_id}")
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
delivery = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)`
    },

    // ─── Test webhook (rate-limited 5/min) ─────────────────────────────────────
    testWebhook: {
        curl: `curl -X POST ${API}/api/v1/webhooks/$WEBHOOK_ID/test \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`${API}/api/v1/webhooks/\${webhookId}/test\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const result = await response.json();
// { success, statusCode, responseBody, error, durationMs }`,

        node: `const response = await fetch(
  \`${API}/api/v1/webhooks/\${webhookId}/test\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const result = await response.json();
if (!result.success) console.error('Test failed:', result.error, 'after', result.durationMs, 'ms');`,

        python: `response = requests.post(
    f'${API}/api/v1/webhooks/{webhook_id}/test',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()
result = response.json()`,

        csharp: `var response = await client.PostAsync(
    $"api/v1/webhooks/{webhookId}/test", null);
response.EnsureSuccessStatusCode();
var result = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post("api/v1/webhooks/{$webhookId}/test", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$result = json_decode($response->getBody(), true);`,

        ruby: `uri = URI("${API}/api/v1/webhooks/#{webhook_id}/test")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
result = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)`
    },

    // ─── Rotate signing secret ──────────────────────────────────────────────────
    regenerateSecret: {
        curl: `curl -X POST ${API}/api/v1/webhooks/$WEBHOOK_ID/regenerate-secret \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`${API}/api/v1/webhooks/\${webhookId}/regenerate-secret\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const { secret } = await response.json();
// Roll this into your verifier AND keep accepting the old secret for a few minutes.`,

        node: `const response = await fetch(
  \`${API}/api/v1/webhooks/\${webhookId}/regenerate-secret\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const { secret: newSecret } = await response.json();`,

        python: `response = requests.post(
    f'${API}/api/v1/webhooks/{webhook_id}/regenerate-secret',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()
new_secret = response.json()['secret']`,

        csharp: `var response = await client.PostAsync(
    $"api/v1/webhooks/{webhookId}/regenerate-secret", null);
response.EnsureSuccessStatusCode();
var body = await response.Content.ReadFromJsonAsync<JsonElement>();
var newSecret = body.GetProperty("secret").GetString();`,

        php: `$response = $client->post("api/v1/webhooks/{$webhookId}/regenerate-secret", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$newSecret = json_decode($response->getBody(), true)['secret'];`,

        ruby: `uri = URI("${API}/api/v1/webhooks/#{webhook_id}/regenerate-secret")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
new_secret = JSON.parse(
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }.body
)['secret']`
    }
};

export default {
    name: 'GuideWebhooks',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>
